
import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logWithTimestamp } from '@/auth/utils/authUtils';

/**
 * Hook to handle authentication session management
 * Listens for auth state changes and provides current session/user info
 */
export const useAuthSession = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    logWithTimestamp("Auth session hook initialized");

    // First set up the auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      logWithTimestamp('Auth state changed:', event);
      
      // Guard against updates after component unmount
      if (!mounted) return;
      
      if (currentSession?.user) {
        logWithTimestamp('User authenticated:', { id: currentSession.user.id, email: currentSession.user.email });
        setSession(currentSession);
        setUser(currentSession.user);
        setIsAuthenticated(true);
      } else if (event === 'SIGNED_OUT') {
        logWithTimestamp('User signed out, clearing session state');
        if (mounted) {
          setSession(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        logWithTimestamp(`Auth event ${event} with no session`);
        if (event !== 'INITIAL_SESSION' && mounted) {
          setSession(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    });

    // Then check for an existing session
    const initializeAuth = async () => {
      try {
        logWithTimestamp("Checking for existing session");
        const { data, error } = await supabase.auth.getSession();
        
        // Guard against updates after component unmount
        if (!mounted) return;
        
        if (error) {
          logWithTimestamp('Error getting session on init:', error);
          setIsAuthenticated(false);
          setSession(null);
          setUser(null);
        } else if (data.session) {
          logWithTimestamp('Existing session found on init for user:', data.session.user.email);
          setSession(data.session);
          setUser(data.session.user);
          setIsAuthenticated(true);
        } else {
          logWithTimestamp('No active session found on init');
          setSession(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        logWithTimestamp('Unexpected error during session check:', err);
        if (mounted) {
          setIsAuthenticated(false);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    
    initializeAuth();

    // Cleanup function
    return () => {
      logWithTimestamp("Cleaning up auth subscription");
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Manual refresh session function
   */
  const refreshSession = async () => {
    logWithTimestamp("Manual session refresh requested");
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        logWithTimestamp('Error refreshing session:', error);
        setIsAuthenticated(false);
        setSession(null);
        setUser(null);
        return;
      }
      
      const hasSession = !!data.session;
      
      if (hasSession) {
        logWithTimestamp('Session found in refresh check');
        setSession(data.session);
        setUser(data.session.user);
        setIsAuthenticated(true);
      } else {
        logWithTimestamp('No session found in refresh check');
        setSession(null);
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      logWithTimestamp('Error refreshing auth status:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    session,
    user,
    isAuthenticated,
    isLoading,
    refreshSession
  };
};
